import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Building2, GitBranch, Shield, ListChecks, Briefcase, UserCheck, Share2, FlaskConical, HeartPulse, Scale, History, Lock } from 'lucide-react';
import { PageHeader } from '../../../components/PageHeader';
import { usePermission } from '../../../auth/AuthContext';
import { authorityApi } from '../../../api/authority';
import { AuthorityNav, Pill } from './authorityUi';

/** Organization & Authority - the normal place to administer who can do and approve what. Technical security lives under Advanced Security. */
export function OrganizationAuthorityHubPage() {
  const advanced = usePermission('admin.security.advanced');
  const health = useQuery({ queryKey: ['authority-health'], queryFn: () => authorityApi.health() });
  const issues = (health.data?.data.checks ?? []).filter((c) => c.severity !== 'ok' && c.count > 0);
  const cards: [string, typeof Building2, string, string][] = [
    ['/admin/authority/positions', Building2, 'Positions', 'Create positions and assign staff. Access follows the position automatically.'],
    ['/admin/authority/reporting', GitBranch, 'Reporting Structure', 'Who reports to whom, and explicit supervisor exceptions.'],
    ['/admin/authority/access-profiles', Shield, 'Access Profiles', 'Plain-language capability bundles, e.g. "Finance - Officer".'],
    ['/admin/authority/approval-rules', ListChecks, 'Approval Rules', 'What each transaction needs, who has final authority, and where it stops.'],
    ['/admin/authority/responsibilities', Briefcase, 'Responsibilities', 'Budget Holders, Project Managers and focal points per project/programme.'],
    ['/admin/authority/acting', UserCheck, 'Acting Appointments', 'Temporary acting in a position - expires automatically.'],
    ['/admin/delegations', Share2, 'Delegations', 'Temporary transfer of specific approval authority.'],
    ['/admin/authority/tester', FlaskConical, 'Authority Tester', 'Who can do this? Who approves that? And why. Explains only.'],
    ['/admin/authority/health', HeartPulse, 'Governance Health', 'Gaps that would block approvals or leave access unexplained.'],
    ['/admin/authority/reconciliation', Scale, 'Reconciliation', 'Move existing staff onto positions safely and compare access before/after.'],
    ['/admin/audit', History, 'Audit', 'Every authority change, who made it and when.'],
  ];
  return (
    <div className="page">
      <PageHeader title="Organization & Authority" description="Employee → Position → Save. VConnect derives normal access and approval authority from the position; nothing is configured user-by-user." />
      <AuthorityNav current="/admin/authority" />
      {issues.length > 0 && (
        <div className="card">
          <h2>Needs attention</h2>
          <ul className="auth-issue-list">
            {issues.map((c) => <li key={c.key}><Pill tone={c.severity === 'critical' ? 'bad' : c.severity === 'warning' ? 'warn' : 'info'}>{c.count}</Pill> {c.title}</li>)}
          </ul>
          <Link to="/admin/authority/health">Open Governance Health →</Link>
        </div>
      )}
      <div className="quick-actions-grid">
        {cards.map(([to, Icon, title, desc]) => (
          <Link key={to} to={to} className="quick-action"><span className="quick-action-icon"><Icon size={18} /></span><div><strong>{title}</strong><div className="muted">{desc}</div></div></Link>
        ))}
        {advanced && (
          <Link to="/admin/authority/advanced" className="quick-action"><span className="quick-action-icon"><Lock size={18} /></span><div><strong>Advanced Security</strong><div className="muted">Technical roles, raw permissions and Exceptional Access. Restricted.</div></div></Link>
        )}
      </div>
    </div>
  );
}

/** Advanced Security: the technical layer, deliberately out of normal administration. */
export function AdvancedSecurityPage() {
  const advanced = usePermission('admin.security.advanced');
  if (!advanced) return <div className="page"><div className="error-state">Advanced Security is restricted.</div></div>;
  return (
    <div className="page">
      <PageHeader title="Advanced Security" description="Technical roles, permission keys and Exceptional Access (user overrides). Normal access should come from positions - use these only for genuine exceptions." />
      <AuthorityNav current="" />
      <div className="quick-actions-grid">
        <Link to="/admin/access" className="quick-action"><div><strong>Users & Exceptional Access</strong><div className="muted">User accounts, retained technical roles and per-user overrides. Every override is audited.</div></div></Link>
        <Link to="/admin/roles" className="quick-action"><div><strong>Technical Roles</strong><div className="muted">Raw role-to-permission mappings. Access Profiles are built on these.</div></div></Link>
        <Link to="/admin/approval-matrix" className="quick-action"><div><strong>Legacy Approval Matrix</strong><div className="muted">Used only by transaction types with no active Approval Rule.</div></div></Link>
      </div>
    </div>
  );
}
